import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, ReceiptText, Send, Eye, Download, X,
  CheckCircle2, FileSpreadsheet, Plus, Trash2, RefreshCw, AlertTriangle,
  Wallet, Banknote, Smartphone, CreditCard, Building, History,
  Paperclip, FileText, Upload, ImageIcon, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import { todayLocalIso } from "@/lib/dateUtils";

const STATUS_COLOR = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  partial: "bg-amber-100 text-amber-900 border-amber-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  combined: "bg-purple-100 text-purple-800 border-purple-200",
  void: "bg-red-100 text-red-800 border-red-200",
};

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "mpesa", label: "M-PESA", icon: Smartphone },
  { value: "credit_card", label: "Credit card", icon: CreditCard },
  { value: "bank_transfer", label: "Bank transfer", icon: Building },
  { value: "cheque", label: "Cheque", icon: ReceiptText },
];

export default function Invoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(null); // invoice obj
  const [includeStakeholders, setIncludeStakeholders] = useState(true);
  const [extraEmails, setExtraEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [payOpen, setPayOpen] = useState(null);  // invoice obj
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("mpesa");
  const [payRef, setPayRef] = useState("");
  const [payDate, setPayDate] = useState(() => todayLocalIso());
  const [paySendReceipt, setPaySendReceipt] = useState(true);
  const [recording, setRecording] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(null);  // invoice obj
  const [historyPayments, setHistoryPayments] = useState([]);
  // ───── Phase 45 — Payment-proof attachments ─────
  const [proofsOpen, setProofsOpen] = useState(null); // invoice obj
  const [uploadingProof, setUploadingProof] = useState(false);
  const [previewProof, setPreviewProof] = useState(null); // {url, mime_type, original_filename}

  const canManage = ["admin", "global_admin", "hospitality_coordinator", "booking_coordinator"].includes(user.role);
  // Phase 75 — combine invoices for a single payment
  const [combineIds, setCombineIds] = useState([]);
  const [combining, setCombining] = useState(false);
  const combinable = (inv) => !["paid", "void", "combined"].includes(inv.status);
  const combineSelected = async () => {
    if (!window.confirm(`Combine ${combineIds.length} invoices into one bill for a single payment? The originals will be marked "combined".`)) return;
    setCombining(true);
    try {
      const { data } = await api.post("/invoices/combine", { invoice_ids: combineIds });
      toast.success(`Combined into invoice #${data.invoice_number}`);
      setCombineIds([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to combine"); }
    finally { setCombining(false); }
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get("/invoices"); setInvoices(data); }
    catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `invoice_${inv.invoice_number}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const previewPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error("Preview failed"); }
  };

  const sendInvoice = async () => {
    if (!sendOpen) return;
    const additional = extraEmails.split(/[\s,;]+/).map(e => e.trim()).filter(Boolean);
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${sendOpen.id}/send`, {
        additional_recipients: additional,
        include_stakeholders: includeStakeholders,
      });
      toast.success(`Invoice sent to ${data.recipients.length} recipient(s)`);
      setSendOpen(null); setExtraEmails(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const markStatus = async (inv, status) => {
    try {
      await api.put(`/invoices/${inv.id}/status?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error("Status update failed"); }
  };

  const deleteInvoice = async (inv) => {
    const warning = inv.status === "paid"
      ? `Invoice #${inv.invoice_number} is marked PAID. Delete anyway? This cannot be undone.`
      : `Delete invoice #${inv.invoice_number} for ${inv.visitor_name}? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Invoice #${inv.invoice_number} deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const regenerateInvoice = async (inv) => {
    const msg = `Rebuild invoice #${inv.invoice_number} from the current booking (reflects any date or rate changes)?\n\nThe invoice will reset to DRAFT status so you can review before resending.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/invoices/${inv.id}/regenerate`);
      toast.success(`Invoice #${inv.invoice_number} rebuilt from booking`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
  };

  const openPayment = (inv) => {
    const eff = inv.adjusted_totals || inv.totals || {};
    const total = (eff.KES || 0) + (eff.USD || 0);
    const due = Math.max(0, total - (inv.amount_paid || 0));
    setPayOpen(inv);
    setPayAmount(String(due || total));
    setPayMethod("mpesa");
    setPayRef("");
    setPayDate(todayLocalIso());
    setPaySendReceipt(!!inv.visitor_email);
  };

  const recordPayment = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setRecording(true);
    try {
      const { data } = await api.post("/housing/payments", {
        invoice_id: payOpen.id,
        amount: amt,
        currency: "KES",
        method: payMethod,
        reference: payRef,
        paid_at: payDate,
        send_receipt: paySendReceipt,
      });
      const status = data.invoice_status;
      const dueLeft = data.invoice_amount_due;
      let msg = `Payment recorded — invoice now ${status}`;
      if (status === "partial") msg = `Partial payment recorded — KES ${dueLeft.toLocaleString()} still due`;
      if (status === "paid") msg = "Payment recorded — invoice fully paid";
      toast.success(msg);
      if (data.receipt_mocked) {
        toast.info("Receipt email mocked (RESEND_API_KEY missing on this environment)", { duration: 4000 });
      }
      setPayOpen(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
    finally { setRecording(false); }
  };

  const openHistory = async (inv) => {
    setHistoryOpen(inv);
    try {
      const { data } = await api.get(`/housing/payments?invoice_id=${inv.id}`);
      setHistoryPayments(data);
    } catch { setHistoryPayments([]); }
  };

  const uploadPaymentProof = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/housing/payments/${p.id}/proof`, fd);
      setHistoryPayments((prev) => prev.map((x) => x.id === p.id ? { ...x, proof_url: data.proof_url } : x));
      toast.success("Payment proof attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Delete this ${p.method} payment of KES ${Number(p.amount).toLocaleString()}? Invoice status will recompute.`)) return;
    try {
      await api.delete(`/housing/payments/${p.id}`);
      toast.success("Payment deleted");
      setHistoryPayments((arr) => arr.filter((x) => x.id !== p.id));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ───── Phase 45 — Payment-proof handlers ─────
  const openProofs = (inv) => setProofsOpen(inv);

  const uploadProof = async (invoiceId, file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("File too large (max 12 MB)");
      return;
    }
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/invoices/${invoiceId}/payment-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success("Payment proof attached");
      // Optimistically update state for snappy UX
      setInvoices((arr) => arr.map((inv) =>
        inv.id === invoiceId
          ? { ...inv, payment_proofs: [...(inv.payment_proofs || []), data] }
          : inv,
      ));
      setProofsOpen((cur) => (cur && cur.id === invoiceId
        ? { ...cur, payment_proofs: [...(cur.payment_proofs || []), data] }
        : cur));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingProof(false);
    }
  };

  const deleteProof = async (invoiceId, proofId) => {
    if (!window.confirm("Remove this payment proof? This cannot be undone.")) return;
    try {
      await api.delete(`/invoices/${invoiceId}/payment-proof/${proofId}`);
      toast.success("Proof removed");
      setInvoices((arr) => arr.map((inv) =>
        inv.id === invoiceId
          ? { ...inv, payment_proofs: (inv.payment_proofs || []).filter((p) => p.id !== proofId) }
          : inv,
      ));
      setProofsOpen((cur) => (cur && cur.id === invoiceId
        ? { ...cur, payment_proofs: (cur.payment_proofs || []).filter((p) => p.id !== proofId) }
        : cur));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const absProofUrl = (url) => (url?.startsWith("http") ? url : `${backendBase}${url}`);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get("/invoices/export/excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url; link.download = "invoices.xlsx";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoices exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  // An invoice is "stale" when the linked booking has been edited AFTER the
  // invoice was last issued or regenerated. The coordinator should hit
  // Regenerate to refresh the line items and totals.
  const isStale = (inv) => {
    if (!inv.booking_updated_at) return false;
    const reference = inv.regenerated_at || inv.issued_at;
    if (!reference) return false;
    return new Date(inv.booking_updated_at) > new Date(reference);
  };
  const staleCount = useMemo(() => invoices.filter(isStale).length, [invoices]);

  return (
    <div className="space-y-6 fade-in" data-testid="invoices-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Invoices</h1>
          <p className="text-[#525C58] mt-1">Bill visitors and stakeholders for stays.</p>
        </div>
        <div className="flex gap-2">
          {canManage && combineIds.length >= 2 && (
            <Button onClick={combineSelected} disabled={combining}
                    className="bg-purple-700 hover:bg-purple-800" data-testid="combine-invoices-btn">
              <CheckCheck className="h-4 w-4 mr-2" /> {combining ? "Combining…" : `Combine ${combineIds.length} into one bill`}
            </Button>
          )}
          <Button variant="outline" onClick={exportExcel} disabled={exporting} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="export-invoices-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> {exporting ? "Exporting…" : "Export to Excel"}
          </Button>
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-lg">
            <span className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-[#40916C]" /> All invoices ({invoices.length})
            </span>
            {staleCount > 0 && (
              <span
                className="text-xs font-medium px-3 py-1.5 rounded-full border bg-amber-50 text-amber-900 border-amber-300 flex items-center gap-1.5"
                data-testid="stale-count-pill"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {staleCount} need{staleCount === 1 ? "s" : ""} refresh
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staleCount > 0 && (
            <div
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="stale-banner"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>{staleCount}</strong> invoice{staleCount === 1 ? "" : "s"} may be out of date — the linked booking was edited
                after the invoice was issued. Click the{" "}
                <RefreshCw className="h-3 w-3 inline-block align-text-bottom" /> button on each row to rebuild from the current
                booking.
              </span>
            </div>
          )}
          {loading ? <div className="py-10 text-center text-[#7A8A84]">Loading…</div> : invoices.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-10">
              No invoices yet. Generate one from a booking.
              <Button variant="ghost" onClick={() => navigate("/bookings")} className="ml-2 text-[#1B4332]">Open bookings</Button>
            </p>
          ) : (
            <Table data-testid="invoices-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Visitor / Tenant</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(inv => {
                  const stale = isStale(inv);
                  return (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className={stale ? "bg-amber-50/40" : ""}>
                    <TableCell className="font-mono font-medium">
                      <span className="flex items-center gap-1.5">
                        {canManage && combinable(inv) && (
                          <input type="checkbox" checked={combineIds.includes(inv.id)}
                                 onChange={(e) => setCombineIds((p) => e.target.checked ? [...p, inv.id] : p.filter((x) => x !== inv.id))}
                                 data-testid={`combine-select-${inv.id}`} />
                        )}
                        #{inv.invoice_number}
                      </span>
                      {inv.status === "combined" && inv.combined_into_number && (
                        <span className="block text-[9px] text-purple-700 font-sans">→ #{inv.combined_into_number}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{inv.visitor_name}</p>
                        {stale && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1"
                            title={`Booking was edited on ${new Date(inv.booking_updated_at).toLocaleString()} — invoice needs a refresh.`}
                            data-testid={`stale-pill-${inv.id}`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Booking changed
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-[#7A8A84]">{inv.visitor_email || "—"}</p>
                    </TableCell>
                    <TableCell className="text-sm text-[#525C58]">{(inv.issued_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-sm text-[#525C58]">{inv.due_at}</TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {Object.entries(inv.totals || {}).filter(([, v]) => v).map(([k, v]) => (
                        <div key={k}>{k} {v.toLocaleString()}</div>
                      ))}
                      {inv.exchange_rate_used && (inv.totals?.USD || 0) > 0 && (
                        <div
                          className="text-[10px] text-[#7A8A84] mt-0.5"
                          title={`Rate stamped when invoice was issued: USD 1 = KES ${Number(inv.exchange_rate_used).toFixed(2)}`}
                          data-testid={`kes-equiv-${inv.id}`}
                        >
                          ≈ KES {((inv.totals?.KES || 0) + (inv.totals?.USD || 0) * inv.exchange_rate_used).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          <span className="ml-1 text-[#A3ACA8]">@ {Number(inv.exchange_rate_used).toFixed(2)}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {inv.amount_paid ? (
                        <>
                          <div className="text-emerald-700 font-medium">KES {Number(inv.amount_paid).toLocaleString()}</div>
                          {inv.amount_due > 0 && <div className="text-[10px] text-red-600">due {Number(inv.amount_due).toLocaleString()}</div>}
                        </>
                      ) : <span className="text-[#7A8A84]">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLOR[inv.status]} border text-xs`}>{inv.status}</Badge>
                      {inv.kind === "lease" && inv.period && (
                        <Badge className="ml-1 bg-purple-50 text-purple-800 border border-purple-200 text-[9px] uppercase">{inv.period}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => previewPdf(inv)} className="text-[#1B4332]" data-testid={`preview-${inv.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv)} className="text-[#1B4332]" data-testid={`download-${inv.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {canManage && (
                        <>
                          {inv.status !== "paid" && inv.status !== "void" && inv.status !== "combined" && (
                            <Button size="sm" variant="ghost" onClick={() => openPayment(inv)} className="text-emerald-700 hover:bg-emerald-50" title="Record payment" data-testid={`pay-${inv.id}`}>
                              <Wallet className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {(inv.amount_paid || 0) > 0 && (
                            <Button size="sm" variant="ghost" onClick={() => openHistory(inv)} className="text-blue-700 hover:bg-blue-50" title="Payment history" data-testid={`history-${inv.id}`}>
                              <History className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openProofs(inv)}
                            className={`relative ${(inv.payment_proofs?.length || 0) > 0 ? "text-indigo-700 hover:bg-indigo-50" : "text-[#525C58] hover:bg-[#F4F4F1]"}`}
                            title={(inv.payment_proofs?.length || 0) > 0 ? `${inv.payment_proofs.length} payment proof(s) attached` : "Attach payment proof (M-PESA, bank slip, PDF)"}
                            data-testid={`proofs-${inv.id}`}
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                            {(inv.payment_proofs?.length || 0) > 0 && (
                              <span
                                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-indigo-600 text-white text-[9px] font-semibold flex items-center justify-center"
                                data-testid={`proofs-count-${inv.id}`}
                              >
                                {inv.payment_proofs.length}
                              </span>
                            )}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => regenerateInvoice(inv)} className="text-amber-700 hover:bg-amber-50" title="Rebuild from booking" data-testid={`regenerate-${inv.id}`}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setSendOpen(inv)} className="text-emerald-700" data-testid={`send-${inv.id}`}>
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteInvoice(inv)} className="text-red-600 hover:bg-red-50" title="Delete invoice" data-testid={`delete-${inv.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Send invoice dialog */}
      <Dialog open={!!sendOpen} onOpenChange={(o) => !o && setSendOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send invoice #{sendOpen?.invoice_number}</DialogTitle>
            <DialogDescription>
              The PDF is attached automatically. Visitor email is included if known.
            </DialogDescription>
          </DialogHeader>
          {sendOpen && (
            <div className="space-y-4">
              <div className="text-sm text-[#525C58] space-y-1">
                <p><strong>Visitor:</strong> {sendOpen.visitor_name} ({sendOpen.visitor_email || "no email"})</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-[#F4F4F1] rounded-lg border border-[#E8E8E6]">
                <Switch checked={includeStakeholders} onCheckedChange={setIncludeStakeholders} data-testid="include-stakeholders-toggle" />
                <Label>Also CC all configured stakeholders</Label>
              </div>
              <div className="space-y-2">
                <Label>Additional recipients</Label>
                <Input value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)} placeholder="agency@example.org, finance@hospital.org" data-testid="extra-emails-input" />
                <p className="text-xs text-[#7A8A84]">Comma-, semicolon-, or space-separated list.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(null)}>Cancel</Button>
            <Button onClick={sendInvoice} disabled={sending} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="confirm-send-btn">
              <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Send invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent className="max-w-lg" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
            <DialogDescription>
              {payOpen && (
                <>Invoice #{payOpen.invoice_number} — {payOpen.visitor_name}<br />
                Total: KES {(payOpen.totals?.KES || 0).toLocaleString()}
                {payOpen.amount_paid ? <> · Already paid: KES {Number(payOpen.amount_paid).toLocaleString()}</> : null}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {payOpen && (payOpen.payment_proofs?.length || 0) > 0 ? (
              <div
                className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900"
                data-testid="pay-dialog-proof-present"
              >
                <CheckCheck className="h-4 w-4 shrink-0" />
                <span className="flex-1">
                  <strong>{payOpen.payment_proofs.length}</strong> payment proof{payOpen.payment_proofs.length === 1 ? "" : "s"} already attached.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-indigo-300 text-indigo-900 hover:bg-indigo-100"
                  onClick={() => { const inv = payOpen; setPayOpen(null); openProofs(inv); }}
                  data-testid="pay-dialog-view-proofs-btn"
                >
                  <Paperclip className="h-3 w-3 mr-1" /> View
                </Button>
              </div>
            ) : payOpen ? (
              <div
                className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
                data-testid="pay-dialog-no-proof"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="flex-1">
                  No payment proof attached yet. Consider uploading one for your records.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                  onClick={() => { const inv = payOpen; setPayOpen(null); openProofs(inv); }}
                  data-testid="pay-dialog-attach-proof-btn"
                >
                  <Paperclip className="h-3 w-3 mr-1" /> Attach
                </Button>
              </div>
            ) : null}
            <div className="space-y-1">
              <Label>Amount (KES)</Label>
              <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount-input" />
            </div>
            <div className="space-y-1">
              <Label>Method</Label>
              <div className="grid grid-cols-5 gap-2">
                {PAYMENT_METHODS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value} type="button"
                    onClick={() => setPayMethod(value)}
                    data-testid={`pay-method-${value}`}
                    className={`p-2 rounded-lg border text-xs flex flex-col items-center gap-1 transition-colors ${
                      payMethod === value
                        ? "bg-[#1B4332] text-white border-[#1B4332]"
                        : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-[10px] font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date</Label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-date-input" />
              </div>
              <div className="space-y-1">
                <Label>Reference {payMethod === "mpesa" ? "(M-PESA code)" : payMethod === "credit_card" ? "(card last 4)" : payMethod === "bank_transfer" ? "(transaction ID)" : "(optional)"}</Label>
                <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder={payMethod === "mpesa" ? "e.g. SK123ABCXY" : ""} data-testid="pay-ref-input" />
              </div>
            </div>
            {payOpen?.visitor_email && (
              <div className="flex items-center gap-2 p-3 bg-[#F4F4F1] rounded-lg border border-[#E8E8E6]">
                <Switch checked={paySendReceipt} onCheckedChange={setPaySendReceipt} data-testid="pay-receipt-toggle" />
                <Label>Email receipt to {payOpen.visitor_email}</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(null)}>Cancel</Button>
            <Button onClick={recordPayment} disabled={recording} className="bg-emerald-700 hover:bg-emerald-800" data-testid="confirm-payment-btn">
              <Wallet className="h-4 w-4 mr-2" /> {recording ? "Recording…" : "Record payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment history dialog */}
      <Dialog open={!!historyOpen} onOpenChange={(o) => !o && setHistoryOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="payment-history-dialog">
          <DialogHeader>
            <DialogTitle>Payment history</DialogTitle>
            <DialogDescription>
              {historyOpen && <>Invoice #{historyOpen.invoice_number} — {historyOpen.visitor_name}</>}
            </DialogDescription>
          </DialogHeader>
          {historyPayments.length === 0 ? (
            <p className="text-sm text-[#7A8A84] py-4 text-center">No payments recorded.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Method</TableHead><TableHead>Reference</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {historyPayments.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell>{p.paid_at}</TableCell>
                    <TableCell><Badge className="bg-blue-50 text-blue-800 border border-blue-200 text-[10px]">{p.method.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-xs text-[#525C58]">{p.reference || "—"}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{p.currency} {Number(p.amount).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      {p.proof_url ? (
                        <Button size="sm" variant="ghost"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${p.proof_url}`, "_blank")}
                                className="text-indigo-700" title="View payment proof" data-testid={`view-proof-${p.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <label className="inline-flex" title="Attach proof (photo / PDF)">
                          <span className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-[#F4F4F1] cursor-pointer text-[#525C58]">
                            <Upload className="h-3.5 w-3.5" />
                          </span>
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                                 onChange={(e) => { uploadPaymentProof(p, e.target.files[0]); e.target.value = ""; }}
                                 data-testid={`upload-proof-${p.id}`} />
                        </label>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/housing/payments/${p.id}/receipt`, "_blank")} className="text-[#1B4332]" title="Download receipt PDF">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePayment(p)} className="text-red-600" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Phase 45 — Payment proofs dialog ─── */}
      <Dialog open={!!proofsOpen} onOpenChange={(o) => !o && setProofsOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="proofs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-indigo-700" />
              Payment proofs
            </DialogTitle>
            <DialogDescription>
              {proofsOpen && (
                <>Invoice #{proofsOpen.invoice_number} — {proofsOpen.visitor_name}. Attach M-PESA screenshots, bank slips, or scanned receipts (images or PDF, max 12 MB).</>
              )}
            </DialogDescription>
          </DialogHeader>

          {proofsOpen && (
            <div className="space-y-4">
              {/* Upload controls — two buttons: gallery + camera */}
              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${uploadingProof ? "opacity-60 pointer-events-none" : "border-[#1B4332] bg-[#1B4332] text-white hover:bg-[#2D6A4F]"}`}
                    data-testid="upload-proof-file-btn"
                  >
                    <Upload className="h-4 w-4" />
                    {uploadingProof ? "Uploading…" : "Choose file / PDF"}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) uploadProof(proofsOpen.id, f);
                      }}
                      disabled={uploadingProof}
                      data-testid="upload-proof-file-input"
                    />
                  </label>
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${uploadingProof ? "opacity-60 pointer-events-none" : "border-[#1B4332] text-[#1B4332] hover:bg-[#F4F4F1]"}`}
                    data-testid="upload-proof-camera-btn"
                  >
                    <Smartphone className="h-4 w-4" />
                    Take photo
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) uploadProof(proofsOpen.id, f);
                      }}
                      disabled={uploadingProof}
                      data-testid="upload-proof-camera-input"
                    />
                  </label>
                </div>
              )}

              {/* Existing proofs gallery */}
              {(proofsOpen.payment_proofs?.length || 0) === 0 ? (
                <div
                  className="border border-dashed border-[#E8E8E6] rounded-lg p-8 text-center text-sm text-[#7A8A84]"
                  data-testid="proofs-empty"
                >
                  <Paperclip className="h-8 w-8 mx-auto mb-2 text-[#C4C9C7]" />
                  No payment proofs attached yet.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="proofs-gallery">
                  {proofsOpen.payment_proofs.map((p) => {
                    const isPdf = p.mime_type === "application/pdf";
                    const isImage = (p.mime_type || "").startsWith("image/");
                    return (
                      <div
                        key={p.id}
                        className="relative border border-[#E8E8E6] rounded-lg overflow-hidden bg-[#FAFAF8] group"
                        data-testid={`proof-${p.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => setPreviewProof(p)}
                          className="w-full h-32 flex items-center justify-center bg-white hover:bg-[#F4F4F1] transition-colors"
                          data-testid={`proof-preview-${p.id}`}
                        >
                          {isImage ? (
                            <img
                              src={absProofUrl(p.url)}
                              alt={p.original_filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : isPdf ? (
                            <div className="flex flex-col items-center gap-1 text-red-700">
                              <FileText className="h-10 w-10" />
                              <span className="text-[10px] font-semibold">PDF</span>
                            </div>
                          ) : (
                            <ImageIcon className="h-10 w-10 text-[#C4C9C7]" />
                          )}
                        </button>
                        <div className="p-2 text-[10px] space-y-0.5">
                          <p className="font-medium truncate" title={p.original_filename}>{p.original_filename}</p>
                          <p className="text-[#7A8A84]">
                            {Math.round((p.size_bytes || 0) / 1024)} KB · {p.uploaded_by_name}
                          </p>
                          <p className="text-[#A3ACA8]">{new Date(p.uploaded_at).toLocaleString()}</p>
                        </div>
                        {canManage && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteProof(proofsOpen.id, p.id)}
                            className="absolute top-1 right-1 h-6 w-6 p-0 text-red-600 bg-white/90 hover:bg-red-50 border border-[#E8E8E6] opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove this proof"
                            data-testid={`delete-proof-${p.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Shortcut: if proofs exist but invoice not yet paid */}
              {canManage && (proofsOpen.payment_proofs?.length || 0) > 0 &&
                proofsOpen.status !== "paid" && proofsOpen.status !== "void" && (
                <div
                  className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900"
                  data-testid="proof-record-payment-shortcut"
                >
                  <CheckCheck className="h-4 w-4 shrink-0" />
                  <span className="flex-1">Ready to record the matching payment?</span>
                  <Button
                    size="sm"
                    className="bg-emerald-700 hover:bg-emerald-800 h-7 text-xs"
                    onClick={() => { const inv = proofsOpen; setProofsOpen(null); openPayment(inv); }}
                    data-testid="proof-goto-payment-btn"
                  >
                    <Wallet className="h-3 w-3 mr-1" /> Record payment
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setProofsOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof preview — full-size image or open PDF in new tab */}
      <Dialog open={!!previewProof} onOpenChange={(o) => !o && setPreviewProof(null)}>
        <DialogContent className="max-w-3xl" data-testid="proof-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{previewProof?.original_filename}</DialogTitle>
            <DialogDescription>
              {previewProof && (
                <>Uploaded by {previewProof.uploaded_by_name} · {new Date(previewProof.uploaded_at).toLocaleString()}</>
              )}
            </DialogDescription>
          </DialogHeader>
          {previewProof && (previewProof.mime_type || "").startsWith("image/") ? (
            <div className="flex items-center justify-center bg-black/5 rounded-lg p-2">
              <img
                src={absProofUrl(previewProof.url)}
                alt={previewProof.original_filename}
                className="max-h-[70vh] w-auto object-contain rounded"
              />
            </div>
          ) : previewProof ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <FileText className="h-16 w-16 text-red-700" />
              <p className="text-sm text-[#525C58]">PDF preview opens in a new tab.</p>
              <Button
                onClick={() => window.open(absProofUrl(previewProof.url), "_blank")}
                className="bg-[#1B4332] hover:bg-[#2D6A4F]"
              >
                <Eye className="h-4 w-4 mr-2" /> Open PDF
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            {previewProof && (
              <Button
                variant="outline"
                onClick={() => window.open(absProofUrl(previewProof.url), "_blank")}
              >
                <Download className="h-4 w-4 mr-2" /> Download
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewProof(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
